import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { getTableHeader, handleCellClick } from './rep-abon-data-grouping';
import RepAbonReportGraph from './rep-abon-report-graph';
import RepAbonReportDetail from './rep-abon-report-detail';
import { Table, Spin } from 'antd';
import './rep-abon-report-total.css';
import { isMobile } from '../../../extensions/design';
import { useExcelData } from "../rep-excel-data-provider";

const chartColors = [
  '#fbc02d', '#1976d2', '#0288d1', '#388e3c', '#d32f2f', '#7b1fa2', '#ffa000',
  '#0288d1', '#388e3c', '#d32f2f', '#7b1fa2', '#ffa000'
];

function RepAbonReportTotal({ filters, data, onRefresh, refreshTrigger, loading }) {
  const [chartType, setChartType] = useState('pie');
  const [detailData, setDetailData] = useState(null);
  const [expandedDetailGroups, setExpandedDetailGroups] = useState(new Set());
  const [selectedCell, setSelectedCell] = useState(null);
  const { setExcelDataTotal } = useExcelData();
  
  // Приводим входные данные к строкам таблицы и считаем "Всего"
  const tableRows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    
    return data.map((item) => {
      let subscriptionSum = 0;
      let subscriptionDiscount = 0;
      let subscriptionPaid = 0;
      let subscriptionDebt = 0;
      let subscriptionSalary = 0;
      let packetSum = 0;
      let packetDiscount = 0;
      let packetPaid = 0;
      let packetDebt = 0;
      let packetSalary = 0;

      if (Array.isArray(item.items)) {
        item.items.forEach(saleItem => {
          if (saleItem.saleType === 2) { // Подписки (subscription)
            subscriptionSum += saleItem.sum || 0;
            subscriptionDiscount += saleItem.discount || 0;
            subscriptionPaid += saleItem.paid || 0;
            subscriptionDebt += saleItem.debt || 0;
            subscriptionSalary += saleItem.salary || 0;
          } else if (saleItem.saleType === 3) { // Пакеты (packet)
            packetSum += saleItem.sum || 0;
            packetDiscount += saleItem.discount || 0;
            packetPaid += saleItem.paid || 0;
            packetDebt += saleItem.debt || 0;
            packetSalary += saleItem.salary || 0;
          }
        });
      }

      const totalSalary = subscriptionSalary + packetSalary;

      return {
        key: item.userId?.toString() || item.employeeName,
        name: item.employeeName,
        userId: item.userId || null,
        subscriptionSum,
        subscriptionDiscount,
        subscriptionPaid,
        subscriptionDebt,
        packetSum,
        packetDiscount,
        packetPaid,
        packetDebt,
        totalSum: subscriptionSum + packetSum,
        totalDiscount: subscriptionDiscount + packetDiscount,
        totalPaid: subscriptionPaid + packetPaid,
        totalDebt: subscriptionDebt + packetDebt,
        salary: totalSalary,
      };
    });
  }, [data]);


const handleRowClick = useCallback((record) => {
  // Пропускаем клик по строке "Всего"
  if (record.key === 'summary-row') return;
  
  // Получаем userId из исходных данных
  const userData = data.find(item => 
    item.userId?.toString() === record.key || item.employeeName === record.name
  );
  
  if (userData && userData.userId) {
    // Открываем детализацию для сотрудника
    const detailDataResult = handleCellClick(data, record.name, [filters.startDate, filters.endDate], 'employee', 'Всего', 
      filters.reportType, tableRows, userData.userId);
    
    if (detailDataResult) {
      setDetailData(detailDataResult);
      setSelectedCell({ 
        rowName: record.name,
        section: 'employee', 
        metric: 'Всего',
        userId: userData.userId
      });
    }
  }
}, [data, filters.reportType, tableRows]);

const rowLabels = useMemo(() => tableRows.map(r => r.name), [tableRows]);

const summaryRow = useMemo(() => {
  const acc = tableRows.reduce((a, r) => {
    a.subscriptionSum += r.subscriptionSum;
    a.subscriptionDiscount += r.subscriptionDiscount;
    a.subscriptionPaid += r.subscriptionPaid;
    a.subscriptionDebt += r.subscriptionDebt;

    a.packetSum += r.packetSum;
    a.packetDiscount += r.packetDiscount;
    a.packetPaid += r.packetPaid;
    a.packetDebt += r.packetDebt;

    a.totalSum += r.totalSum;
    a.totalDiscount += r.totalDiscount;
    a.totalPaid += r.totalPaid;
    a.totalDebt += r.totalDebt;

    a.salary += r.salary;
    return a;
  }, {
    subscriptionSum: 0, subscriptionDiscount: 0, subscriptionPaid: 0, subscriptionDebt: 0,
    packetSum: 0, packetDiscount: 0, packetPaid: 0, packetDebt: 0,
    totalSum: 0, totalDiscount: 0, totalPaid: 0, totalDebt: 0,
    salary: 0
  });

  return {
    key: 'summary-row',
    name: 'Всего',
    ...acc
  };
}, [tableRows]);

  const fullDataSource = useMemo(() => [...tableRows, summaryRow], [tableRows, summaryRow]);

  const renderNumber = (value) => (value ? value.toLocaleString() : '');

  const isCellSelected = useCallback((rowName, section, metric) => {
    if (!selectedCell) return false;

    return (
      selectedCell.rowName === rowName &&
      selectedCell.section === section &&
      selectedCell.metric === metric
    );
  }, [selectedCell]);

const handleCellClickCallback = useCallback((rowName, section, metric, userId = null) => {
  setSelectedCell({ rowName, section, metric });

  setDetailData({ 
    items: [], 
    dates: [filters.startDate, filters.endDate],
    type: metric, 
    vidName: section, 
    kassaName: rowName, 
    userId 
  });
}, []);

  const closeDetail = useCallback(() => {
    setDetailData(null);
    setExpandedDetailGroups(new Set());
    setSelectedCell(null);
  }, []);

  useEffect(() => {
    closeDetail();
  }, [refreshTrigger]);

  const showDebtColumns = filters.periodType !== 1;

  const columns = [
    {
      title: getTableHeader ? getTableHeader(filters.reportType) : 'Сотрудник',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 140,
      render: (text) => <span>{text}</span>,
      onCell: () => ({ className: 'cell-center clickable-cell group-sep' }),
    },

    // Абонементы
    {
      title: <span className="header-cell">Сумма</span>,
      dataIndex: 'subscriptionSum',
      key: 'subscriptionSum',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row && row.key === 'summary-row')
          ? ''
          : (isCellSelected(row.name, 'Абонементы', 'sale') ? 'selected-cell' : 'clickable-cell'),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Абонементы', 'sale', row.userId),
      }),
    },
    {
      title: <span className="header-cell">Скидка</span>,
      dataIndex: 'subscriptionDiscount',
      key: 'subscriptionDiscount',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row && row.key === 'summary-row')
          ? ''
          : (isCellSelected(row.name, 'Абонементы', 'sale') ? 'selected-cell' : 'clickable-cell'),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Абонементы', 'sale', row.userId),
      }),
    },
    {
      title: <span className="header-cell">Оплата</span>,
      dataIndex: 'subscriptionPaid',
      key: 'subscriptionPaid',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: [
          (row && row.key === 'summary-row')
            ? ''
            : (isCellSelected(row.name, 'Абонементы', 'paid') ? 'selected-cell' : 'clickable-cell'),
          (showDebtColumns ? '' : 'group-sep')
        ].filter(cls => cls !== '').join(' '),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Абонементы', 'paid', row.userId),
      }),
    },
    ...(showDebtColumns ? [{
      title: <span className="header-cell">Долг</span>,
      dataIndex: 'subscriptionDebt',
      key: 'subscriptionDebt',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: [ (row && row.key === 'summary-row')
          ? ''
          : (isCellSelected(row.name, 'Абонементы', 'paid') ? 'selected-cell' : 'clickable-cell'),
          (showDebtColumns ? 'group-sep' : '')
        ].filter(cls => cls !== '').join(' '),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Абонементы', 'paid', row.userId),
      }),
    }] : []),

    // Пакеты
    {
      title: <span className="header-cell">Сумма</span>,
      dataIndex: 'packetSum',
      key: 'packetSum',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row && row.key === 'summary-row')
          ? ''
          : (isCellSelected(row.name, 'Пакеты', 'sale') ? 'selected-cell' : 'clickable-cell'),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Пакеты', 'sale', row.userId),
      }),
    },
    {
      title: <span className="header-cell">Скидка</span>,
      dataIndex: 'packetDiscount',
      key: 'packetDiscount',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row && row.key === 'summary-row')
          ? ''
          : (isCellSelected(row.name, 'Пакеты', 'sale') ? 'selected-cell' : 'clickable-cell'),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Пакеты', 'sale', row.userId),
      }),
    },
    {
      title: <span className="header-cell">Оплата</span>,
      dataIndex: 'packetPaid',
      key: 'packetPaid',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: [
          (row && row.key === 'summary-row')
            ? ''
            : (isCellSelected(row.name, 'Пакеты', 'paid') ? 'selected-cell' : 'clickable-cell'),
          (showDebtColumns ? '' : 'group-sep')
        ].filter(cls => cls !== '').join(' '),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Пакеты', 'paid', row.userId),
      }),
    },
    ...(showDebtColumns ? [{
      title: <span className="header-cell">Долг</span>,
      dataIndex: 'packetDebt',
      key: 'packetDebt',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: [
          (row && row.key === 'summary-row')
            ? 'group-sep'
            : (isCellSelected(row.name, 'Пакеты', 'paid') ? 'selected-cell' : 'clickable-cell'),
        (showDebtColumns ? 'group-sep' : '')
        ].filter(cls => cls !== '').join(' '),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Пакеты', 'paid', row.userId),
      }),
    }] : []),

    // Всего
    {
      title: <span className="header-cell">Сумма</span>,
      dataIndex: 'totalSum',
      key: 'totalSum',
      align: 'right',
      width: 100,
      render: (value) => (
        <span>
          {renderNumber(value)}
        </span>
      ),
      onCell: (row) => ({
        className: (row && row.key === 'summary-row')
          ? ''
          : (isCellSelected(row.name, 'Всего', 'sale') ? 'selected-cell' : 'clickable-cell'),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Всего', 'sale', row.userId),
      }),
    },
    {
      title: <span className="header-cell">Скидка</span>,
      dataIndex: 'totalDiscount',
      key: 'totalDiscount',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row && row.key === 'summary-row')
          ? ''
          : (isCellSelected(row.name, 'Всего', 'sale') ? 'selected-cell' : 'clickable-cell'),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Всего', 'sale', row.userId),
      }),
    },
    {
      title: <span className="header-cell">Оплата</span>,
      dataIndex: 'totalPaid',
      key: 'totalPaid',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: [
          (row && row.key === 'summary-row') 
            ? '' 
            : (isCellSelected(row.name, 'Всего', 'paid') ? 'selected-cell' : 'clickable-cell'),
          (showDebtColumns ? '' : 'group-sep')
        ].filter(cls => cls !== '').join(' '),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Всего', 'paid', row.userId),
      }),
    },
    ...(showDebtColumns ? [{
      title: <span className="header-cell">Долг</span>,
      dataIndex: 'totalDebt',
      key: 'totalDebt',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: [
          (row && row.key === 'summary-row')
            ? ''
            : (isCellSelected(row.name, 'Всего', 'paid') ? 'selected-cell' : 'clickable-cell'),
          (showDebtColumns ? 'group-sep' : '')
        ].filter(cls => cls !== '').join(' '),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Всего', 'paid', row.userId),
      }),
    }] : []),

    // Зарплата
    {
      title: <span className="header-cell">Зарплата</span>,
      dataIndex: 'salary',
      key: 'salary',
      align: 'right',
      width: 100,
      render: (value, row, idx) => (
        <span>
          {row.name !== 'Всего' && (
          <span
            className="report-total-colorbox"
            style={{ background: chartColors[idx % chartColors.length] }}
          />
          )}
          {renderNumber(value)}
        </span>
      ),
      onCell: (row) => ({
        className: (row && row.key === 'summary-row')
          ? ''
          : (isCellSelected(row.name, 'Всего', 'salary') ? 'selected-cell' : 'clickable-cell'),
        onClick: (row && row.key === 'summary-row')
          ? undefined
          : () => handleCellClickCallback(row.name, 'Всего', 'salary', row.userId),
      }),
    },
  ];
  const prevExcelState = useRef(null);
  useEffect(() => {
    if (!columns?.length || !fullDataSource?.length) return;

    const newState = {
      title: 'Общий отчет',
      columnsCount: columns.length,
      rowsCount: fullDataSource.length,
    };

    // Проверяем, изменились ли данные реально
    if (
      prevExcelState.current &&
      prevExcelState.current.columnsCount === newState.columnsCount &&
      prevExcelState.current.rowsCount === newState.rowsCount
    ) {
      return; // ничего не делаем — данные те же
    }

    prevExcelState.current = newState;

    setExcelDataTotal({
      title: 'Общий отчет',
      columns,
      data: fullDataSource,
    });

  }, [columns, fullDataSource]);

  const customHeader = () => {
    const subscriptionColSpan = showDebtColumns ? 4 : 3;
    const packetColSpan = showDebtColumns ? 4 : 3;
    const totalColSpan = showDebtColumns ? 4 : 3;

    return (
      <thead>
        <tr>
          <th
            rowSpan={2}
            className="header-cell header-cell-main group-sep"
            onClick={closeDetail}
          >
            {getTableHeader ? getTableHeader(filters.reportType) : 'Сотрудник'}
          </th>

          <th colSpan={subscriptionColSpan} className="header-cell group-sep">Абонементы</th>
          <th colSpan={packetColSpan} className="header-cell group-sep">Пакеты</th>
          <th colSpan={totalColSpan} className="header-cell group-sep">Всего</th>
          <th rowSpan={2} className="header-cell">Зарплата</th>
        </tr>
        <tr>
          {/* Абонементы */}
          <th className="header-cell">Сумма</th>
          <th className="header-cell">Скидка</th>
          <th className={showDebtColumns ? "header-cell" : "header-cell group-sep"}>Оплата</th>
          {showDebtColumns && <th className="header-cell group-sep">Долг</th>}
          {/* Пакеты */}
          <th className="header-cell">Сумма</th>
          <th className="header-cell">Скидка</th>
          <th className={showDebtColumns ? "header-cell" : "header-cell group-sep"}>Оплата</th>
          {showDebtColumns && <th className="header-cell group-sep">Долг</th>}
          {/* Всего */}
          <th className="header-cell">Сумма</th>
          <th className="header-cell">Скидка</th>
          <th className={showDebtColumns ? "header-cell" : "header-cell group-sep"}>Оплата</th>
          {showDebtColumns && <th className="header-cell group-sep">Долг</th>}
        </tr>
      </thead>
    );
  };

  const mobileHeader = () => (
    <thead>
      <tr>
        <th
          rowSpan={1}
          className="header-cell header-cell-main"
          onClick={closeDetail}
        >
          {getTableHeader ? getTableHeader(filters.reportType) : 'Сотрудник'}
        </th>
        <th className="header-cell">Сумма</th>
        <th className="header-cell">Скидка</th>
        <th className="header-cell">Оплата</th>
        {showDebtColumns && <th className="header-cell">Долг</th>}
        <th className="header-cell">Зарплата</th>
      </tr>
    </thead>
  );

  if (isMobile)
    return (
      <div className="abon-total report-total-flex">
        {loading ? (
        <div className="report-loading">
          <Spin size="large" />
        </div>
      ) : (
        <>
        <div className="date-period">
          Период {filters.startDate?.format('DD.MM.YY')} - {filters.endDate?.format('DD.MM.YY')}
        </div>
        <div className="report-total-main">
          <div>
            <Table
              columns={[
                {
                  title: getTableHeader ? getTableHeader(filters.reportType) : 'Сотрудник',
                  dataIndex: 'name',
                  key: 'name',
                  fixed: 'left',
                  width: 100,
                  onHeaderCell: () => ({ onClick: closeDetail, className: 'header-cell header-cell-main' }),
                },
                {
                  title: 'Сумма',
                  dataIndex: 'totalSum',
                  key: 'totalSum',
                  align: 'right',
                  width: 45,
                  render: renderNumber,
                  onCell: (row) => ({
                    className: (row && row.key === 'summary-row') ? '' : (isCellSelected(row.name, 'Всего', 'sale') ? 'selected-cell' : 'clickable-cell'),
                    onClick: (row && row.key === 'summary-row') ? undefined : () => handleCellClickCallback(row.name, 'Всего', 'sale', row.userId),
                  }),
                },
                {
                  title: 'Скидка',
                  dataIndex: 'totalDiscount',
                  key: 'totalDiscount',
                  align: 'right',
                  width: 45,
                  render: renderNumber,
                  onCell: (row) => ({
                    className: (row && row.key === 'summary-row') ? '' : (isCellSelected(row.name, 'Всего', 'sale') ? 'selected-cell' : 'clickable-cell'),
                    onClick: (row && row.key === 'summary-row') ? undefined : () => handleCellClickCallback(row.name, 'Всего', 'sale', row.userId),
                  }),
                },
                {
                  title: 'Оплата',
                  dataIndex: 'totalPaid',
                  key: 'totalPaid',
                  align: 'right',
                  width: 50,
                  render: renderNumber,
                  onCell: (row) => ({
                    className: (row && row.key === 'summary-row') ? '' : (isCellSelected(row.name, 'Всего', 'paid') ? 'selected-cell' : 'clickable-cell'),
                    onClick: (row && row.key === 'summary-row') ? undefined : () => handleCellClickCallback(row.name, 'Всего', 'paid', row.userId),
                  }),
                },
                 ...(showDebtColumns ? [{
                   title: 'Долг',
                   dataIndex: 'totalDebt',
                   key: 'totalDebt',
                   align: 'right',
                   width: 50,
                   render: renderNumber,
                   onCell: (row) => ({
                     className: (row && row.key === 'summary-row') ? '' : (isCellSelected(row.name, 'Всего', 'paid') ? 'selected-cell' : 'clickable-cell'),
                     onClick: (row && row.key === 'summary-row') ? undefined : () => handleCellClickCallback(row.name, 'Всего', 'paid', row.userId),
                   }),
                 }] : []),
                {
                  title: 'Зарплата',
                  dataIndex: 'salary',
                  key: 'salary',
                  align: 'right',
                  width: 50,
                  render: renderNumber,
                  onCell: (row) => ({
                    className: (row && row.key === 'summary-row') ? '' : (isCellSelected(row.name, 'Всего', 'salary') ? 'selected-cell' : 'clickable-cell'),
                    onClick: (row && row.key === 'summary-row') ? undefined : () => handleCellClickCallback(row.name, 'Всего', 'salary', row.userId),
                  }),
                },
              ]}
              dataSource={fullDataSource}
              pagination={false}
              bordered
              size="small"
              rowClassName={(row) => row && row.key === 'summary-row' ? 'total-row' : ''}
              components={{ header: { wrapper: mobileHeader } }}
              className="report-total-table-normal"
            />
          </div>
          {detailData && (
            <div className="report-total-detail-wrapper">
              <RepAbonReportDetail
                detailData={detailData.items}
                onClose={closeDetail}
                detailParams={{
                  startDate: filters.startDate,
                  endDate: filters.endDate,
                  employeeType: filters.employeeType,
                  periodType: filters.periodType,
                  type: detailData.type,
                  vidName: detailData.vidName,
                  productIds: filters.specificProductIds,
                  selectedRowName: detailData.kassaName || detailData.periodName || 'Всего',
                  userId: detailData.userId
                }}
                expandedGroups={expandedDetailGroups}
                setExpandedGroups={setExpandedDetailGroups}
              />
            </div>
          )}
          {!detailData && (
              <RepAbonReportGraph
              data={tableRows.map((row) => ({ name: row.name, total: row.salary }))}
              chartType={chartType}
              setChartType={setChartType}
              reportType={filters.reportType}
            />
          )}
        </div>
      </>
      )}
      </div>
    );

  return (
    <div className="abon-total report-total-flex">
      {loading ? (
      <div className="report-loading">
        <Spin size="large" />
      </div>
    ) : (
      <>
      <RepAbonReportGraph
        data={tableRows.map((row) => ({ name: row.name, total: row.salary }))}
        chartType={chartType}
        setChartType={setChartType}
        reportType={filters.reportType}
      />
      <div className="report-total-main">
        <div className="print-title">
          Отчёт по продажам абонементов, пакетов за период {filters.startDate?.format("DD.MM.YYYY")} — {filters.endDate?.format("DD.MM.YYYY")}
        </div>
        <div>
          <Table
            columns={columns}
            dataSource={fullDataSource}
            pagination={false}
            bordered
            size="small"
            rowClassName={(row) => row && row.key === 'summary-row' ? 'total-row' : ''}
            components={{ header: { wrapper: customHeader } }}
            className="report-total-table-normal"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
            })}
          />
        </div>
        {detailData && (
          <div className="report-total-detail-wrapper">
            <RepAbonReportDetail
              detailData={detailData.items}
              onClose={closeDetail}
              detailParams={{
                startDate: filters.startDate,
                endDate: filters.endDate,
                employeeType: filters.employeeType,
                periodType: filters.periodType,
                type: detailData.type,
                vidName: detailData.vidName,
                productIds: filters.specificProductIds,
                selectedRowName: detailData.kassaName || detailData.periodName || 'Всего',
                userId: detailData.userId
              }}
              expandedGroups={expandedDetailGroups}
              setExpandedGroups={setExpandedDetailGroups}
            />
          </div>
        )}
      </div>
      </>
    )}
    </div>
  );
}

export default RepAbonReportTotal;
